import { differenceInDays } from 'date-fns';

/**
 * Catalogue of in-app tours.
 *
 * A tour is an overlay walkthrough of UI that is already on screen, offered
 * behind a help button that carries a dot until it has been clicked once. Two
 * types share the same engine:
 *
 * - `onboarding` — evergreen. The dot shows to anyone who has not clicked yet,
 *   and the tour stays available through the button forever after.
 * - `announcement` — tied to one released change. Its dot expires
 *   `ANNOUNCEMENT_WINDOW_DAYS` after `releasedAt`, so a user who arrives a year
 *   later is not flagged with last year's news. To re-announce something, add
 *   a new entry with a new `id` — seen-state is keyed by id, so everyone's dot
 *   lights again. There is deliberately no version field, because bumping one
 *   out of habit after a typo fix would re-flag every user in the product.
 *
 * A step's `target` names a `data-tour` attribute in the app. Targets are
 * resolved when the tour opens and may legitimately be absent — a sidebar is
 * unmounted when closed, not merely hidden — and a step whose target is missing
 * is dropped from that run, unless the step has a `prepare` stage that will
 * produce it. Anchoring it somewhere arbitrary instead reads as a bug, so a
 * tour shows only the steps it can actually point at.
 *
 * This file is pure data: copy, target names, and the *names* of the stages a
 * tour needs (`setup`, `prepare`). What a stage does is supplied by the adapter
 * that mounts `AppTour` beside the feature — see `AppTour` for the contract.
 * Nothing from the app may be imported here, and a test enforces it: that is
 * what keeps a fifth tour from turning this file into a hub that knows every
 * feature.
 *
 * Copy here is user-facing and product-approved — the steps and the `button`
 * that opens them, whose tooltip and label should say *which* tour it opens.
 * Treat it like UI text, not like comments — and keep it short. A step is glanced at, not read: long
 * descriptions get skipped wholesale, taking the short ones after them along
 * too. `MAX_DESCRIPTION_LENGTH` is enforced by the tests.
 *
 * ## Why a dot on a button, rather than opening by itself
 *
 * A tour that opens unbidden reaches everyone and interrupts everyone; one
 * hidden behind a plain button interrupts nobody and reaches almost nobody. The
 * dot sits between: it is noticed by most people who visit the view, costs a
 * glance, and goes away the first time they act on it. Nothing here opens over
 * a user's work.
 *
 * Whether the dot earns its keep is measurable. `AppTour` reports every start,
 * advance, dismissal and completion to the `tour` event on `dataLayer`; the
 * number to watch is starts against visits to the view. If almost nobody clicks
 * a badged button, the badge is not the fix — the copy or the placement is.
 *
 * Two caveats on reading that number. Cookiebot gates GTM, so users who decline
 * cookies are invisible and the counts undercount; and seen-state lives in
 * `localStorage`, so it measures browsers rather than people.
 */

export const TOUR_TYPE = Object.freeze({
	ONBOARDING: 'onboarding',
	ANNOUNCEMENT: 'announcement',
});

/**
 * The longest a step's description may be. Not a style preference: past roughly
 * this length a reader decides not to start.
 */
export const MAX_DESCRIPTION_LENGTH = 90;

/** How long an announcement stays interesting after its release date. */
export const ANNOUNCEMENT_WINDOW_DAYS = 60;

/** Tour ids, so consumers don't pass raw strings around. */
export const TOUR_ID = Object.freeze({
	COLLECTION_VIEW: 'collection-view',
	AI_DETECTION: 'ai-detection',
	VIDEO_RESULTS: 'video-results',
	VIDEO_NEW_PROJECT: 'video-new-project',
	VIDEO_CORRECTING: 'video-correcting',
});

const COLLECTION_VIEW_TOUR = {
	id: TOUR_ID.COLLECTION_VIEW,
	type: TOUR_TYPE.ONBOARDING,
	button: { tooltip: 'Tour this view', label: 'Tour the collection view' },
	// Steps 3, 5 and 6 point into the Layers sidebar, which is unmounted when
	// closed; `ProjectTour` supplies the stage that opens it.
	setup: 'layerView',
	steps: [
		{
			target: 'collection-picker',
			title: 'Pick a collection',
			description: 'Choose a collection to view its detections on the map.',
			placement: 'bottom',
		},
		{
			target: 'ai-detection',
			title: 'Run a new detection',
			description: 'Pick a collection, choose an area, then run.',
			placement: 'bottom',
		},
		{
			target: 'layer-view',
			title: 'Everything drawn on the map',
			description: 'Turn layers on or off, reorder them, open their settings.',
			placement: 'right',
		},
		{
			target: 'map-legend',
			title: 'What the colors mean',
			description: 'Which color on the map belongs to which object.',
			placement: 'left',
		},
		{
			target: 'collection-objects',
			title: 'Filter and color by value',
			description: 'Filter detections by value, or recolor an object to spot a pattern.',
			placement: 'right',
		},
		{
			target: 'mode-switch',
			title: 'Model view is still here',
			description: 'The same project, analyzed by a trained model instead.',
			placement: 'right',
		},
	],
};

/**
 * Walks the detection wizard page by page. It lives inside the wizard, so it
 * never opens the wizard itself — each step names the page it needs and
 * `ObjectPickerTour` turns to it. It ends on the Run button rather than
 * pressing it: a detection run costs compute and changes the project, and a
 * tour must never start one. The progress dialog is described, not shown, for
 * the same reason.
 */
const AI_DETECTION_TOUR = {
	id: TOUR_ID.AI_DETECTION,
	type: TOUR_TYPE.ONBOARDING,
	button: { tooltip: 'How this wizard works', label: 'Tour the detection wizard' },
	steps: [
		{
			target: 'picker-collection',
			title: 'Choose a collection',
			description:
				'A saved set of objects to detect. Manage collections on the Collections page.',
			placement: 'top',
			prepare: 'collection',
		},
		{
			target: 'picker-area',
			title: 'Choose where to look',
			description: 'Draw an area on the map, or search the entire project.',
			placement: 'top',
			prepare: 'area',
		},
		{
			target: 'picker-run',
			title: 'Confirm and run',
			description: 'Starts the job. A progress dialog then follows it until results land.',
			placement: 'top',
			prepare: 'run',
		},
	],
};

/**
 * The finished results of a video analysis. Several steps only exist for some
 * projects — a line only in line-crossing mode, the map only with flight data,
 * versions only after a run again, the changes list only after a fix — and
 * the engine drops whichever is missing. It never enters correction mode: the
 * correcting toolbar is a tour of its own.
 */
const VIDEO_RESULTS_TOUR = {
	id: TOUR_ID.VIDEO_RESULTS,
	type: TOUR_TYPE.ONBOARDING,
	button: { tooltip: 'Tour the results', label: 'Tour the video results' },
	steps: [
		{
			target: 'video-count',
			title: 'Your count',
			description:
				'The total for the way of counting you picked. It updates as you change things.',
			placement: 'left',
		},
		{
			target: 'video-count-modes',
			title: 'Count it another way',
			description: 'Switch between the total, a line crossing, and the most in view at once.',
			placement: 'left',
		},
		{
			target: 'video-changes',
			title: 'Your changes',
			description: 'Every fix to this version is listed here. Undo any of them.',
			placement: 'left',
		},
		{
			target: 'video-sensitivity',
			title: 'Cut false detections',
			description: 'Raise it to cut false detections. The count updates as you slide.',
			placement: 'left',
		},
		{
			target: 'video-size-filter',
			title: 'Filter by size',
			description: 'Leave out what is too small or too large to be what you counted.',
			placement: 'left',
		},
		{
			target: 'video-line',
			title: 'Move the line',
			description: 'Drag the line or its ends. The count is recalculated for the new line.',
			placement: 'bottom',
		},
		{
			target: 'video-flight-map',
			title: 'Where the drone flew',
			description: 'The flight path with each animal on the ground. Click to jump there.',
			placement: 'left',
		},
		{
			target: 'video-detections',
			title: 'What the video shows',
			description: 'Dots, boxes or off. Turn trails, the line and the map on or off.',
			placement: 'top',
		},
		{
			target: 'video-correct',
			title: 'Correct detections',
			description:
				'Add missed animals, remove wrong ones, or ask the assistant to look again.',
			placement: 'top',
		},
		{
			target: 'video-versions',
			title: 'Earlier versions',
			description: 'Every run again is a new version. Switch between them here.',
			placement: 'top',
		},
		{
			target: 'video-details-export',
			title: 'Details and export',
			description: 'See what was analysed. Download a PDF report, CSV of counts or the map.',
			placement: 'top',
		},
		{
			target: 'video-in-view',
			title: 'Jump to the busy part',
			description: 'How many are in view over time. Click to jump to that moment.',
			placement: 'top',
		},
	],
};

/**
 * A new video project once the video is in: what to find, which part to
 * analyse, and the upload. Not offered on the file step before it, which has
 * its own "Before you start" panel. How to count is chosen on the result, so
 * that tour covers it. It ends on the Upload button rather than pressing it.
 */
const VIDEO_NEW_PROJECT_TOUR = {
	id: TOUR_ID.VIDEO_NEW_PROJECT,
	type: TOUR_TYPE.ONBOARDING,
	button: { tooltip: 'Tour this step', label: 'Tour the video setup' },
	steps: [
		{
			target: 'video-steps',
			title: 'Where you are',
			description: 'Four steps from video to count. Done steps show what you chose.',
			placement: 'right',
		},
		{
			target: 'video-subject',
			title: 'What to count',
			description:
				'The AI suggests a word from the video. Change it and it picks the model that fits.',
			placement: 'left',
		},
		{
			target: 'video-trim',
			title: 'Analyse only what matters',
			description: 'Drag the handles or type the times. A shorter part finishes sooner.',
			placement: 'left',
		},
		{
			target: 'video-upload',
			title: 'Upload and analyse',
			description:
				'Keep this tab open until the upload ends. The analysis then starts by itself.',
			placement: 'left',
		},
	],
};

/**
 * The correction toolbar on a video, offered from the toolbar itself once the
 * user is correcting — the results tour points at the button that opens it. It
 * describes the tools without choosing one. The assistant exists once the
 * analysis is finished, and Run again only for a general search with fixes, so
 * those steps drop out elsewhere.
 */
const VIDEO_CORRECTING_TOUR = {
	id: TOUR_ID.VIDEO_CORRECTING,
	type: TOUR_TYPE.ONBOARDING,
	button: { tooltip: 'How correcting works', label: 'Tour the correction tools' },
	steps: [
		{
			target: 'video-add',
			title: 'Add what it missed',
			description:
				'Click an animal with no dot (A) or box it (B). It is then followed through the video.',
			placement: 'bottom',
		},
		{
			target: 'video-remove',
			title: 'Remove a wrong one',
			description: 'Pick Remove (R), then click its dot. It goes from every frame.',
			placement: 'bottom',
		},
		{
			target: 'video-assistant',
			title: 'Ask the assistant',
			description:
				'Look again checks this frame for missed animals; you choose what to apply. Costs credits.',
			placement: 'bottom',
		},
		{
			target: 'video-run-again',
			title: 'Run again',
			description: 'Analyse again with your fixes as examples. It becomes a new version.',
			placement: 'left',
		},
		{
			target: 'video-done',
			title: 'Leave correcting',
			description: 'Close the toolbar any time. Each fix was saved as you made it.',
			placement: 'bottom',
		},
	],
};

export const TOURS = Object.freeze([
	COLLECTION_VIEW_TOUR,
	AI_DETECTION_TOUR,
	VIDEO_RESULTS_TOUR,
	VIDEO_NEW_PROJECT_TOUR,
	VIDEO_CORRECTING_TOUR,
]);

/** The tour with this id, or `undefined`. */
export const getTour = tourId => TOURS.find(tour => tour.id === tourId);

/**
 * Whether an announcement is still inside its window. Onboarding tours never
 * expire, so they always pass. An announcement missing `releasedAt` is treated
 * as expired rather than shown forever — a missing date is a mistake, and the
 * quiet failure is the safer one.
 */
export const isWithinWindow = (tour, now = Date.now()) => {
	if (tour?.type !== TOUR_TYPE.ANNOUNCEMENT) return true;
	if (!tour.releasedAt) return false;

	// An unparseable date gives NaN, and NaN compares false — expired.
	return differenceInDays(now, new Date(tour.releasedAt)) < ANNOUNCEMENT_WINDOW_DAYS;
};
